import {html, LitElement, css} from 'lit';
import {customElement, property, query} from 'lit/decorators.js';
import {
  Engine,
  Render,
  Runner,
  Svg,
  Bodies,
  Vector,
  World,
  Mouse,
  MouseConstraint,
  Body,
  Events,
  Query,
} from 'matter-js';
// eslint-disable-next-line node/no-unpublished-import
import physicsSVGs from './physics-svg.json';
import {CANVAS_SCALE, PHYSICS_NAVIGATE_EVENT} from './utils';

const WALL_THICKNESS = 100;
const MAX_CANVAS_WIDTH = 1800;
const HERO_TRANSPARENT_CLASS = 'transparent';
const NO_CENTER_BLOCK_HASH = '#nocenterblock';

@customElement('projects-physics')
export class ProjectsPhysicsElement extends LitElement {
  walls: Body[] = [];

  items: Body[] = [];

  centerBlock: Body = Bodies.rectangle(0, 0, 100, 100, {
    chamfer: {radius: 10},
    isStatic: true,
    render: {fillStyle: 'rgba(255, 0, 0, 0)'},
  });

  _hero = document.getElementById('hero');

  @property({type: Boolean, reflect: true})
  release = false;

  @query('canvas')
  _canvas: HTMLCanvasElement | undefined;

  static get styles() {
    return css`
      :host {
        position: fixed;
      }
    `;
  }

  static outOfArea(canvas: HTMLCanvasElement, body: Body) {
    const buffer = 1000;
    return (
      body.position.y < -buffer ||
      body.position.y > canvas.height + buffer ||
      body.position.x < -buffer ||
      body.position.x > canvas.width + buffer
    );
  }

  firstUpdated() {
    window.addEventListener(
      'resize',
      () => {
        this._onResize();
      },
      false
    );
    if (!this._canvas) return;
    this._canvas.width =
      Math.min(MAX_CANVAS_WIDTH, window.innerWidth) * CANVAS_SCALE;
    this._canvas.height =
      (this._canvas.width / window.innerWidth) * window.innerHeight;
    this._canvas.style.width = `${window.innerWidth}px`;
    this._canvas.style.height = `${window.innerHeight}px`;
    const engine = Engine.create();
    const world = engine.world;
    const render = Render.create({
      canvas: this._canvas,
      engine: engine,
      options: {
        width: this._canvas.width,
        height: this._canvas.height,
        wireframes: false,
        background: 'transparent',
      },
    });
    Render.run(render);
    const runner = Runner.create();
    Runner.run(runner, engine);
    this.walls = [
      Bodies.rectangle(
        render.canvas.width / 2,
        render.canvas.height + WALL_THICKNESS / 2,
        render.canvas.width,
        WALL_THICKNESS,
        {isStatic: true}
      ), // bottom
      Bodies.rectangle(
        render.canvas.width + WALL_THICKNESS / 2,
        render.canvas.height / 2,
        WALL_THICKNESS,
        render.canvas.height,
        {isStatic: true}
      ), // right
      Bodies.rectangle(
        -WALL_THICKNESS / 2,
        render.canvas.height / 2,
        WALL_THICKNESS,
        render.canvas.height,
        {isStatic: true}
      ), // left
    ];
    World.add(world, this.walls);

    physicsSVGs.forEach(item => {
      const vertexSets: Vector[][] = [];
      const path = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'path'
      );
      path.setAttribute('d', item.path);
      const points = Svg.pathToVertices(path, 30);
      vertexSets.push(points);
      const scale = this._getScale();
      const addBody = () => {
        const body = Bodies.fromVertices(
          Math.random() * (render.canvas.width - 200) + 100,
          -100,
          vertexSets,
          {
            render: {
              sprite: {
                xScale: scale,
                yScale: scale,
                texture: `/physics-images/${item.id}.png`,
              },
            },
            label: `${item.id}`,
          },
          true
        );
        Body.scale(body, scale, scale);
        World.add(world, body);
        this.items.push(body);
      };
      addBody();
    });

    if (window.location.hash !== NO_CENTER_BLOCK_HASH) {
      World.add(world, this.centerBlock);
    }

    const mouse = Mouse.create(render.canvas);
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse: mouse,
    });
    World.add(world, mouseConstraint);

    Events.on(mouseConstraint, 'enddrag', event => {
      const diff =
        (event.mouse.mousedownPosition.x - event.mouse.mouseupPosition.x) ** 2 +
        (event.mouse.mousedownPosition.y - event.mouse.mouseupPosition.y) ** 2;
      if (diff < 10) {
        this._onBodyClicked(event.body);
      }
    });

    Events.on(mouseConstraint, 'mousemove', event => {
      const hits = Query.point(this.items, (event as any).mouse.position);
      if (hits.length > 0) {
        document.body.style.cursor = 'pointer';
        hits[0].render.opacity = 0.6;
      } else {
        document.body.style.cursor = 'default';
        this.items.forEach(item => {
          item.render.opacity = 1;
        });
      }
    });

    this._onResize();
    this._recycleBodies();
  }

  updated(changedProps: Map<any, any>) {
    if (changedProps.has('release') && changedProps.get('release')) {
      Body.setPosition(this.centerBlock, {
        x: this.centerBlock.position.x,
        y: -100,
      });
      this.items.forEach(item => {
        Body.setStatic(item, false);
      });
      if (this._hero) this._hero.classList.remove(HERO_TRANSPARENT_CLASS);
      if (!this._canvas) return;
      this._onResize(true);
      const frames = 60;
      let t = 0;
      const dy = (this._canvas.height / 2 + 100) / frames;
      const anim = () => {
        Body.setPosition(this.centerBlock, {
          x: this.centerBlock.position.x,
          y: this.centerBlock.position.y + dy,
        });
        t++;
        if (t !== frames) {
          window.requestAnimationFrame(anim);
        }
      };
      anim();
    }
  }

  _recycleBodies() {
    this.items.forEach(item => {
      if (!this._canvas) return;
      const outOfArea = ProjectsPhysicsElement.outOfArea(this._canvas, item);
      if (!outOfArea) return;
      Body.setPosition(item, {x: window.innerWidth / 2, y: -100});
      Body.setVelocity(item, {x: 0, y: 0});
    });
    window.setTimeout(this._recycleBodies.bind(this), 1000);
  }

  _getScale() {
    return Math.min(1.6, (window.innerWidth / 960) ** 0.7);
  }

  _onBodyClicked(body: Body) {
    if (body.label === 'Rectangle Body') return;
    if (this._hero) this._hero.classList.add(HERO_TRANSPARENT_CLASS);
    if (!this._canvas) return;
    Body.setStatic(body, true);
    this.items.forEach(item => {
      item.render.opacity = 1;
    });
    const frames = 60;
    const sigmoid = (x: number) => 1 / (1 + Math.exp(-x));
    const timingf = (t: number, frames: number) =>
      sigmoid(((t - frames / 2) * 14) / frames);
    const goalAngle = 0;
    const goalTopPosition = (this._canvas.height / 5) * CANVAS_SCALE;
    const goalPosition = {x: this._canvas.width / 2, y: goalTopPosition};
    const angleTimeline = Array(frames)
      .fill(null)
      .map((_, i) => {
        return (
          body.angle * (1 - timingf(i, frames)) + goalAngle * timingf(i, frames)
        );
      });
    const positionTimeline = Array(frames)
      .fill(null)
      .map((_, i) => {
        return {
          x:
            body.position.x * (1 - timingf(i, frames)) +
            goalPosition.x * timingf(i, frames),
          y:
            body.position.y * (1 - timingf(i, frames)) +
            goalPosition.y * timingf(i, frames),
        };
      });
    let t = 0;
    const canvasWidth = this._canvas.width;
    const anim = () => {
      if (t === frames) {
        const windowCanvasRatio =
          (window.innerWidth * CANVAS_SCALE) / canvasWidth;
        this.dispatchEvent(
          new CustomEvent(PHYSICS_NAVIGATE_EVENT, {
            detail: {
              url: `/projects/${body.label}/`,
              pos: {
                x: (body.bounds.min.x * windowCanvasRatio) / CANVAS_SCALE,
                y: (body.bounds.min.y * windowCanvasRatio) / CANVAS_SCALE,
                w:
                  ((body.bounds.max.x - body.bounds.min.x) *
                    windowCanvasRatio) /
                  CANVAS_SCALE,
                h:
                  ((body.bounds.max.y - body.bounds.min.y) *
                    windowCanvasRatio) /
                  CANVAS_SCALE,
              },
            },
            bubbles: true,
            composed: true,
          })
        );
        return;
      }
      Body.setAngle(body, angleTimeline[t]);
      Body.setPosition(body, {
        x: positionTimeline[t].x,
        y: positionTimeline[t].y,
      });
      t++;
      window.requestAnimationFrame(anim);
    };
    anim();
  }

  _adjustCenterBlockSize() {
    if (!this._canvas) return;
    if (!this._hero) return;
    const rect = this._hero.getBoundingClientRect();
    Body.setPosition(this.centerBlock, {
      x: this._canvas.width / 2,
      y: this._canvas.height / 2,
    });
    const {min, max} = this.centerBlock.bounds;
    const ratio = window.innerWidth > 600 ? 0.7 : 0.4;
    const scaleX = ((rect.width * CANVAS_SCALE) / (max.x - min.x)) * ratio;
    const scaleY = ((rect.height * CANVAS_SCALE) / (max.y - min.y)) * ratio;
    Body.scale(this.centerBlock, scaleX, scaleY);
  }

  _onResize(ignoreCenterBlock = false) {
    if (!this._canvas) return;
    this._canvas.width =
      Math.min(MAX_CANVAS_WIDTH, window.innerWidth) * CANVAS_SCALE;
    this._canvas.height =
      (this._canvas.width / window.innerWidth) * window.innerHeight;
    this._canvas.style.width = `${window.innerWidth}px`;
    this._canvas.style.height = `${window.innerHeight}px`;

    Body.setPosition(this.walls[0], {
      x: this._canvas.width / 2,
      y: this._canvas.height + WALL_THICKNESS / 2,
    });
    Body.setPosition(this.walls[1], {
      x: this._canvas.width + WALL_THICKNESS / 2 + 50,
      y: this._canvas.height / 2,
    });
    Body.setPosition(this.walls[2], {
      x: -WALL_THICKNESS / 2 - 50,
      y: this._canvas.height / 2,
    });
    Body.scale(
      this.walls[0],
      this._canvas.width /
        (this.walls[0].bounds.max.x - this.walls[0].bounds.min.x),
      1
    );
    Body.scale(
      this.walls[1],
      1,
      this._canvas.height /
        (this.walls[1].bounds.max.y - this.walls[1].bounds.min.y)
    );
    Body.scale(
      this.walls[2],
      1,
      this._canvas.height /
        (this.walls[2].bounds.max.y - this.walls[2].bounds.min.y)
    );

    if (!ignoreCenterBlock) this._adjustCenterBlockSize();

    const scale = this._getScale();
    this.items.forEach(item => {
      if (!item.render.sprite) return;
      Body.scale(
        item,
        scale / item.render.sprite.xScale,
        scale / item.render.sprite.xScale
      );
      item.render.sprite.xScale = scale;
      item.render.sprite.yScale = scale;
    });
  }

  render() {
    return html` <canvas></canvas> `;
  }
}
